import { customFetch } from "@workspace/api-client-react";

// Vídeo no produto (Pedro, 24/10/2026): 1 por produto, até 30 s, no Profissional e no Ultra, escolhido pelo mesmo botão
// das fotos e na ordem que o lojista der. O arquivo vai do navegador direto para o armazenamento (URL assinada) e o
// servidor faz o WebM, o MP4 de reserva (o que o WhatsApp aceita) e 4 capas de quadros sorteados.
export const LIMITE_VIDEO_SEGUNDOS = 30;
export const LIMITE_VIDEO_BYTES = 300 * 1024 * 1024;
// O servidor confere com 1 s de folga: o celular arredonda a duração do corte.
const FOLGA_SEGUNDOS = 1;

export type StatusDoVideo = "enviando" | "inserindo" | "pronto" | "falhou";

/** O vídeo como o painel vê (GET /api/products/videos/:id e o `video` de GET /api/products). */
export type EstadoDoVideo = {
  id: string;
  status: StatusDoVideo;
  motivo: string | null;
  duracao: number | null;
  capas: string[] | null;
  capa: number;
  posicao: number;
  webm: string | null;
  mp4: string | null;
  // Vídeo pronto que segue no cardápio até este entrar (troca em andamento ou que falhou).
  anterior?: { capa: string; duracao: number | null } | null;
};

/** O vídeo como o cardápio recebe: só pronto e com o recurso da loja. */
export type VideoNoCatalogo = { webm: string; mp4: string; capa: string; duracao: number | null; posicao: number };

export type MidiaDoProduto = { tipo: "foto"; url: string } | { tipo: "video"; video: VideoNoCatalogo };

function texto(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

export function videoDoCatalogo(v: unknown): VideoNoCatalogo | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const webm = texto(o.webm), mp4 = texto(o.mp4), capa = texto(o.capa);
  if (!capa || (!webm && !mp4)) return null;
  return {
    webm: webm ?? "",
    mp4: mp4 ?? "",
    capa,
    duracao: typeof o.duracao === "number" && Number.isFinite(o.duracao) ? o.duracao : null,
    posicao: typeof o.posicao === "number" && Number.isFinite(o.posicao) ? Math.max(0, Math.floor(o.posicao)) : 0,
  };
}

export function estadoDoVideo(v: unknown): EstadoDoVideo | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const id = texto(o.id);
  const status = o.status;
  if (!id || (status !== "enviando" && status !== "inserindo" && status !== "pronto" && status !== "falhou")) return null;
  const capas = Array.isArray(o.capas) ? o.capas.filter((c): c is string => typeof c === "string" && c.length > 0) : null;
  const anterior = o.anterior && typeof o.anterior === "object" ? o.anterior as Record<string, unknown> : null;
  return {
    id,
    status,
    motivo: texto(o.motivo),
    duracao: typeof o.duracao === "number" && Number.isFinite(o.duracao) ? o.duracao : null,
    capas: capas && capas.length > 0 ? capas : null,
    capa: typeof o.capa === "number" && o.capa >= 0 ? Math.floor(o.capa) : 0,
    posicao: typeof o.posicao === "number" && o.posicao >= 0 ? Math.floor(o.posicao) : 0,
    webm: texto(o.webm),
    mp4: texto(o.mp4),
    anterior: anterior && texto(anterior.capa)
      ? { capa: anterior.capa as string, duracao: typeof anterior.duracao === "number" ? anterior.duracao : null }
      : null,
  };
}

/** Fotos e vídeo na ordem do lojista: o vídeo entra no índice `posicao` (no fim, se passar do tamanho). */
export function midiasDoProduto(fotos: string[], video: VideoNoCatalogo | null): MidiaDoProduto[] {
  const lista: MidiaDoProduto[] = fotos.map((url) => ({ tipo: "foto", url }));
  if (video) lista.splice(Math.min(video.posicao, lista.length), 0, { tipo: "video", video });
  return lista;
}

/** A imagem que representa o produto (card, carrinho, sugestão): a capa quando o vídeo é o primeiro ou não há foto. */
export function imagemPrincipal(fotoPrincipal: string | null | undefined, video: VideoNoCatalogo | null): string | null {
  if (video && (video.posicao === 0 || !fotoPrincipal)) return video.capa;
  return fotoPrincipal || null;
}

const EXTENSOES: Record<string, string> = {
  mp4: "video/mp4", m4v: "video/x-m4v", mov: "video/quicktime", qt: "video/quicktime", webm: "video/webm",
  "3gp": "video/3gpp", "3g2": "video/3gpp2", mkv: "video/x-matroska", avi: "video/x-msvideo",
};

function extensao(nome: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(nome || "");
  return m ? m[1]!.toLowerCase() : "";
}

export function ehArquivoDeVideo(f: File): boolean {
  if (f.type) return f.type.startsWith("video/");
  return extensao(f.name) in EXTENSOES;
}

/** O tipo que vai no pedido e no PUT. Alguns Android mandam o arquivo sem tipo: sai pela extensão. */
export function tipoDoVideo(f: File): string {
  if (f.type && f.type.startsWith("video/")) return f.type;
  return EXTENSOES[extensao(f.name)] ?? "video/mp4";
}

export function formatarDuracao(segundos: number | null | undefined): string {
  if (segundos == null || !Number.isFinite(segundos)) return "";
  const s = Math.max(0, Math.round(segundos));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function passaDoLimite(segundos: number | null): boolean {
  return segundos != null && segundos > LIMITE_VIDEO_SEGUNDOS + FOLGA_SEGUNDOS;
}

/**
 * Duração lida no navegador, antes de enviar. null quando ele não abre o arquivo (o MOV do iPhone no Chrome do Android,
 * por exemplo) ou demora: aí quem confere é o servidor.
 */
export function duracaoDoArquivo(f: File, limiteMs = 8000): Promise<number | null> {
  return new Promise((resolve) => {
    let url = "";
    try { url = URL.createObjectURL(f); } catch { resolve(null); return; }
    const v = document.createElement("video");
    let feito = false;
    const fim = (d: number | null) => {
      if (feito) return;
      feito = true;
      clearTimeout(t);
      v.removeAttribute("src");
      try { v.load(); } catch { /* nada */ }
      URL.revokeObjectURL(url);
      resolve(d);
    };
    const t = setTimeout(() => fim(null), limiteMs);
    v.preload = "metadata";
    v.muted = true;
    v.onloadedmetadata = () => fim(Number.isFinite(v.duration) && v.duration > 0 ? v.duration : null);
    v.onerror = () => fim(null);
    v.src = url;
  });
}

export type PedidoDeEnvio = { id: string; url: string; headers: Record<string, string>; expiraEm?: string };

export function pedirEnvio(tamanho: number, tipo: string): Promise<PedidoDeEnvio> {
  return customFetch<PedidoDeEnvio>("/api/products/videos/envio", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tamanho, tipo }),
  });
}

export async function avisarEnviado(id: string): Promise<EstadoDoVideo | null> {
  return estadoDoVideo(await customFetch<unknown>(`/api/products/videos/${encodeURIComponent(id)}/enviado`, { method: "POST" }));
}

export async function lerEstadoDoVideo(id: string): Promise<EstadoDoVideo | null> {
  return estadoDoVideo(await customFetch<unknown>(`/api/products/videos/${encodeURIComponent(id)}`, { method: "GET" }));
}

/** PUT do arquivo cru na URL assinada, com o progresso (o fetch não mostra o progresso do envio). */
export function subirArquivo(
  pedido: PedidoDeEnvio,
  arquivo: File,
  onProgresso: (pct: number) => void,
): { pronto: Promise<void>; cancelar: () => void } {
  const xhr = new XMLHttpRequest();
  const pronto = new Promise<void>((resolve, reject) => {
    xhr.open("PUT", pedido.url);
    for (const [k, v] of Object.entries(pedido.headers || {})) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total > 0) onProgresso(Math.min(100, Math.round((e.loaded / e.total) * 100))); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`status=${xhr.status}`)));
    xhr.onerror = () => reject(new Error("rede"));
    xhr.onabort = () => reject(new Error("cancelado"));
    xhr.send(arquivo);
  });
  return { pronto, cancelar: () => xhr.abort() };
}
